import React, { useState } from "react";
import { X, Check, Zap, ArrowRight, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  MONTHLY_PRICE, YEARLY_PRICE, YEARLY_AS_MONTHLY, YEARLY_SAVING_PCT,
  PLAN_FEATURES, upgradeReasonText, planFeatureText,
} from "../lib/plans";

export default function UpgradeModal({ open, onClose, reason }) {
  const [billing, setBilling] = useState("monthly");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const upgrade = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/stripe/create-checkout-session", { billing, flow: "app" });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout — please try again.");
      setSubmitting(false);
    }
  };

  const monthlyDisplay = billing === "monthly" ? MONTHLY_PRICE : YEARLY_AS_MONTHLY;
  const notInFree = PLAN_FEATURES.filter((f) => f.basic === false).map((f) => f.label);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
        {/* Header — contextual reason */}
        <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4">
          <div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-[#7C3AED]/10 px-2.5 py-1 mb-2">
              <Zap className="w-3 h-3 text-[#7C3AED]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#7C3AED] font-bold">Upgrade to Premium</span>
            </div>
            <p className="text-sm text-[#0F172A] font-medium leading-snug">{upgradeReasonText(reason)}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 -mr-1 -mt-1 flex-shrink-0" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 pb-6">
          {/* Billing toggle */}
          <div className="flex items-center gap-1 p-1 bg-secondary rounded-xl border border-border w-fit mb-4">
            {[["monthly", "Monthly"], ["yearly", "Yearly"]].map(([b, label]) => (
              <button key={b} type="button" onClick={() => setBilling(b)}
                className={`px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                  billing === b ? "bg-white text-[#0F172A] shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}>
                {label}
                {b === "yearly" && (
                  <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded font-bold ${
                    billing === "yearly" ? "bg-[#10B981] text-white" : "bg-[#10B981]/15 text-[#10B981]"
                  }`}>−{YEARLY_SAVING_PCT}%</span>
                )}
              </button>
            ))}
          </div>

          {/* Premium card — navy with indigo glow (matches Signup) */}
          <div className="relative rounded-2xl border border-indigo-500 ring-1 ring-indigo-500/25 bg-[#0F172A] overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-500 to-transparent opacity-70" />
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)" }} />
            <div className="p-5 relative">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.2em] text-indigo-400 mb-1.5">PREMIUM</div>
                  <div className="font-display font-black text-3xl tracking-tight text-white">
                    ${monthlyDisplay}<span className="text-base font-bold text-white/40"> /mo</span>
                  </div>
                  <div className="font-mono text-[11px] text-white/40 mt-1">
                    {billing === "monthly" ? "billed monthly" : `$${YEARLY_PRICE} billed yearly`}
                  </div>
                </div>
                <span className="font-mono text-[8px] px-1.5 py-0.5 rounded bg-indigo-600 text-white tracking-wider">POPULAR</span>
              </div>
              <div className="h-px bg-white/[0.08] mb-4" />
              <div className="space-y-2.5">
                {PLAN_FEATURES.map((f) => (
                  <div key={f.label} className="flex items-center gap-2.5">
                    <Check className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                    <span className="text-[12px] text-white/80">
                      {planFeatureText(f, "premium")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Free comparison — derived, so it can't drift */}
          {notInFree.length > 0 && (
            <p className="text-[11px] text-muted-foreground text-center mt-3 leading-relaxed">
              Not on the free plan: {notInFree.join(" · ")}
            </p>
          )}

          {/* CTA — real Stripe checkout */}
          <button onClick={upgrade} disabled={submitting}
            className="w-full mt-4 flex items-center justify-center gap-2 rounded-xl bg-[#0025D8] hover:bg-[#001CB3] text-white py-3.5 font-bold text-sm shadow-lg shadow-[#0025D8]/20 transition-all disabled:opacity-60 disabled:cursor-not-allowed">
            {submitting
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to checkout…</>
              : <>Upgrade — ${monthlyDisplay}/mo <ArrowRight className="w-4 h-4" /></>}
          </button>
          <p className="text-center font-mono text-[10px] text-muted-foreground mt-2.5">
            Secure payment via Stripe · cancel anytime
          </p>
        </div>
      </div>
    </div>
  );
}
